import React from 'react';
import PropTypes from 'prop-types';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { LivisGradientBarChartProps } from '../types/chart-types';
import { t } from 'i18next';

/**
 * LivisGradientBarChart - A customizable horizontal bar chart with gradient fills
 *
 * This component creates horizontal bar charts with beautiful gradient fills using Highcharts.
 * It's specifically designed for displaying categorical data with visual appeal and customization
 * options for enterprise dashboards.
 *
 * Features:
 * - Horizontal bar orientation for better category label readability
 * - Gradient fill effects with customizable colors
 * - Configurable data labels with flexible positioning
 * - Rounded corners for modern appearance
 * - Responsive design with adjustable dimensions
 * - Clean, minimal styling without legends or export buttons
 * 
 * @param props - Component configuration and data
 * @returns Rendered Highcharts bar chart component
 */
const LivisGradientBarChart: React.FC<LivisGradientBarChartProps> = ({
    categories, // Array of category names for the Y-axis (left side)
    data, // Array of numeric values corresponding to each category
    barColors = ['#EA4343', '#FA61AD'], // Gradient colors [start, end] - defaults to red to pink
    height = 200, // Chart height in pixels
    title = '', // Chart title (empty by default for clean look)
    yAxisTitle = '', // Y-axis label (usually not needed for horizontal bars)
    maxPointWidth = 35, // Maximum width of individual bars in pixels
    borderRadius = 12, // Corner radius for rounded bar ends (modern styling)
    dataLabelAlign = 'left', // Alignment of data labels ('left', 'right', 'center')
    dataLabelInside = false, // Whether to place labels inside or outside bars
    style = {}, // Additional CSS styles for the chart container
}) => {
    // Debug logging for development (should be removed in production)
    console.log('y axis title', yAxisTitle);

    /**
     * Highcharts configuration object
     * Defines all visual and behavioral aspects of the chart
     */
    const options: Highcharts.Options = {
        // Chart container configuration
        chart: {
            type: 'bar', // Horizontal bar chart type
            height, // Use the provided height prop
            // Spacing around the chart area for better visual balance
            spacingTop: 20,
            spacingRight: 20,
            spacingBottom: 30,
            spacingLeft: 20,
        },

        // Chart title configuration
        title: { text: title },

        // X-axis configuration (horizontal axis, shows data values)
        xAxis: {
            categories, // Category names displayed on the left
            lineWidth: 0, // Remove axis line for cleaner look
            tickWidth: 0, // Remove tick marks
            opposite: false, // Keep labels on the left side
            labels: {
                align: 'right', // Right-align category labels
                x: -10, // Horizontal offset from axis
                y: 4, // Vertical offset for better alignment
                style: {
                    fontSize: 'var(--livis-font-size-xs)', // Use font variable
                    color: '#69696E', // Subtle gray color
                },
            },
        },

        // Y-axis configuration (vertical axis, shows categories)
        yAxis: {
            title: { text: yAxisTitle }, // Optional axis title
        },

        // Plot options defining how bars are rendered
        plotOptions: {
            bar: {
                // Data label configuration
                dataLabels: {
                    enabled: true, // Show values on bars
                    align: dataLabelAlign, // Use provided alignment
                    inside: dataLabelInside, // Position inside or outside bars
                },

                // Gradient fill configuration
                color: {
                    // Linear gradient from left to right
                    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                    stops: [
                        [0, barColors[0]], // Start color at 0% position
                        [1, barColors[1]], // End color at 100% position
                    ],
                },

                // Bar sizing and spacing
                maxPointWidth: 60, // Maximum bar width regardless of chart size
                borderRadius, // Rounded corners using provided radius
                groupPadding: 0.1, // Space between different series (10%)
                pointPadding: 0.05, // Space between individual bars (5%)
                pointWidth: undefined, // Let Highcharts calculate optimal width
            },
        },

        // Disable unnecessary UI elements for clean appearance
        legend: { enabled: false }, // No legend needed for single series
        exporting: { enabled: false }, // Disable export buttons
        credits: { enabled: false }, // Remove Highcharts watermark

        // Data series configuration
        series: [
            {
                name: 'Count', // Series name (not displayed due to disabled legend)
                type: 'bar', // Confirm bar chart type
                // Process data to ensure numeric values with 2 decimal precision
                data: data.map((v) => (typeof v === 'number' ? parseFloat(v.toFixed(2)) : v)),
            },
        ],
    lang: {
      noData: `${t("No records found")}`, // Message when no data is available
    },
    };

    // Render the Highcharts component with configured options
    return <HighchartsReact highcharts={Highcharts} options={options} />;
};

/**
 * PropTypes validation for development and debugging
 * Ensures proper data types and required props are provided
 */
LivisGradientBarChart.propTypes = {
    categories: PropTypes.array.isRequired, // Must provide category labels
    data: PropTypes.array.isRequired, // Must provide data values

    // Custom validator for barColors to ensure exactly 2 color strings
    barColors: (props, propName, componentName) => {
        const value = props[propName];
        if (value === undefined) return null; // Optional prop

        // Validate array with exactly 2 string elements
        if (!Array.isArray(value) || value.length !== 2 || !value.every((v) => typeof v === 'string')) {
            return new Error(
                `Invalid prop \`${propName}\` supplied to \`${componentName}\`. Validation failed. Expected a tuple of exactly two strings.`
            );
        }
        return null;
    },

    // Standard PropTypes for remaining props
    height: PropTypes.number,
    title: PropTypes.string,
    yAxisTitle: PropTypes.string,
    maxPointWidth: PropTypes.number,
    borderRadius: PropTypes.number,
    dataLabelAlign: PropTypes.oneOf(['left', 'right', 'center']), // Restrict to valid alignments
    dataLabelInside: PropTypes.bool,
    style: PropTypes.object, // Additional CSS styles
};

export default LivisGradientBarChart;
